// components/ui/Modal/index.tsx

import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "../../../lib/cn.js";

export interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title?: string;
    children: React.ReactNode;
    size?: "sm" | "md" | "lg" | "xl" | "full" | "auto";
    showCloseButton?: boolean;
    closeOnOverlayClick?: boolean;
    closeOnEscape?: boolean;
    className?: string;
    headerClassName?: string;
    bodyClassName?: string;
    footer?: React.ReactNode;
    footerClassName?: string;
    overlayClassName?: string;
    animation?: "fade" | "slide" | "scale" | "none";
    maxHeight?: string;
    minHeight?: string;
}

const Modal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    title,
    children,
    size = "md",
    showCloseButton = true,
    closeOnOverlayClick = true,
    closeOnEscape = true,
    className = "",
    headerClassName = "",
    bodyClassName = "",
    footer,
    footerClassName = "",
    overlayClassName = "",
    animation = "fade",
    maxHeight = "90vh",
    minHeight = "auto",
}) => {
    const modalRef = useRef<HTMLDivElement>(null);
    const [isMounted, setIsMounted] = useState(false);

    // سایزهای مودال
    const sizeClasses = {
        sm: "max-w-sm",
        md: "max-w-md",
        lg: "max-w-xl",
        xl: "max-w-2xl",
        full: "max-w-[95vw]",
        auto: "max-w-auto",
    };

    // انیمیشن‌ها
    const getAnimationClasses = () => {
        switch (animation) {
            case "fade":
                return {
                    overlay: "transition-opacity duration-300",
                    modal: "transition-all duration-300",
                    open: "opacity-100",
                    closed: "opacity-0",
                };
            case "slide":
                return {
                    overlay: "transition-opacity duration-300",
                    modal: "transition-all duration-300 ease-out",
                    open: "translate-y-0 opacity-100",
                    closed: "translate-y-8 opacity-0",
                };
            case "scale":
                return {
                    overlay: "transition-opacity duration-300",
                    modal: "transition-all duration-300 ease-out",
                    open: "scale-100 opacity-100",
                    closed: "scale-95 opacity-0",
                };
            default:
                return {
                    overlay: "",
                    modal: "",
                    open: "",
                    closed: "",
                };
        }
    };

    // جلوگیری از اسکرول در پس‌زمینه
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = "hidden";
            setIsMounted(true);
        } else {
            document.body.style.overflow = "unset";
            const timer = setTimeout(() => {
                setIsMounted(false);
            }, 300);
            return () => clearTimeout(timer);
        }
        return () => {
            document.body.style.overflow = "unset";
        };
    }, [isOpen]);

    // بستن با کلید Escape
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (closeOnEscape && event.key === "Escape" && isOpen) {
                onClose();
            }
        };
        document.addEventListener("keydown", handleEscape);
        return () => document.removeEventListener("keydown", handleEscape);
    }, [isOpen, onClose, closeOnEscape]);

    // فوکوس روی مودال
    useEffect(() => {
        if (isOpen && modalRef.current) {
            setTimeout(() => {
                modalRef.current?.focus();
            }, 100);
        }
    }, [isOpen]);

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (closeOnOverlayClick && e.target === e.currentTarget) {
            onClose();
        }
    };

    if (!isOpen && !isMounted) return null;

    const anim = getAnimationClasses();
    const isOpenClass = isOpen ? anim.open : anim.closed;

    // محاسبه ارتفاع بدنه
    const getBodyStyle = () => {
        if (!footer) {
            // اگر فوتر نداریم، کل ارتفاع رو بدنه می‌گیره
            return {
                maxHeight: `calc(${maxHeight} - 80px)`, // 80px برای هدر
            };
        }
        // اگر فوتر داریم، ارتفاع هدر + فوتر رو کم می‌کنیم
        return {
            maxHeight: `calc(${maxHeight} - 160px)`, // 80px هدر + 80px فوتر
        };
    };

    return createPortal(
        <div
            className={cn(
                "fixed inset-0 z-[1000] flex items-center justify-center p-4",
                anim.overlay,
                overlayClassName
            )}
            onClick={handleOverlayClick}
            role="dialog"
            aria-modal="true"
            aria-labelledby={title ? "modal-title" : undefined}
        >
            {/* پس‌زمینه تیره */}
            <div
                className={cn(
                    "absolute inset-0 bg-black/50 backdrop-blur-sm",
                    anim.overlay,
                    isOpenClass
                )}
            />

            {/* مودال */}
            <div
                ref={modalRef}
                className={cn(
                    "relative bg-white rounded-2xl shadow-2xl w-full flex flex-col",
                    sizeClasses[size],
                    anim.modal,
                    isOpenClass,
                    className
                )}
                style={{
                    maxHeight: maxHeight,
                    minHeight: minHeight !== "auto" ? minHeight : undefined,
                }}
                tabIndex={-1}
            >
                {/* هدر مودال */}
                {(title || showCloseButton) && (
                    <div
                        className={cn(
                            "flex items-center justify-between px-5 py-4 flex-shrink-0",
                            headerClassName
                        )}
                    >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                            {title && (
                                <h2
                                    id="modal-title"
                                    className="text-xl font-bold text-neutral-800 truncate"
                                >
                                    {title}
                                </h2>
                            )}
                        </div>

                        {showCloseButton && (
                            <button
                                onClick={onClose}
                                className="p-2 rounded-xl hover:bg-neutral-100 transition-all duration-200 group flex-shrink-0"
                                aria-label="بستن"
                            >
                                <X className="w-5 h-5 text-neutral-500 group-hover:text-neutral-800 transition-colors" />
                            </button>
                        )}
                    </div>
                )}

                {/* بدنه مودال - با اسکرول */}
                <div
                    className={cn(
                        "flex-1 overflow-y-auto px-5",
                        bodyClassName
                    )}
                    style={getBodyStyle()}
                >
                    {children}
                </div>

                {/* فوتر مودال */}
                {footer && (
                    <div
                        className={cn(
                            "px-5 py-4 border-t border-neutral-100 flex-shrink-0",
                            footerClassName
                        )}
                    >
                        {footer}
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
};

export default Modal;